import type { NavigateFunction } from 'react-router-dom';
import { encodeFiltersToParams } from '../../../utils/encodeUtils';
import type { AnalyticsConfig } from './analyticsConfig';
import type { ScoreDistributionSlice } from './analyticsTypes';

type SummaryFilter = {
  name: string;
  operator: string;
  type: string;
  value: string | number;
  isEvidence: false;
};

function clearSummaryFilterParams(params: URLSearchParams) {
  const keysToDelete: string[] = [];
  for (const key of params.keys()) {
    if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => params.delete(key));
  params.delete('f');
}

export function buildScoreBucketFilters(
  slice: Pick<ScoreDistributionSlice, 'minScore' | 'maxScore' | 'isLastBucket'>,
  scoreField: string,
) {
  const filters: SummaryFilter[] = [
    {
      name: scoreField,
      operator: 'gte',
      type: 'number',
      value: slice.minScore,
      isEvidence: false,
    },
  ];

  if (slice.isLastBucket) {
    filters.push({
      name: scoreField,
      operator: 'lte',
      type: 'number',
      value: slice.maxScore,
      isEvidence: false,
    });
  } else {
    filters.push({
      name: scoreField,
      operator: 'lt',
      type: 'number',
      value: slice.maxScore,
      isEvidence: false,
    });
  }

  return filters;
}

export function navigateToFilteredTable(navigate: NavigateFunction, filters: SummaryFilter[]) {
  const params = new URLSearchParams(window.location.search);
  params.delete('display');
  clearSummaryFilterParams(params);

  const filterParams = encodeFiltersToParams(filters);
  filterParams.forEach((value, key) => params.set(key, value));

  navigate({ pathname: window.location.pathname, search: `?${params.toString()}` });
}

export function navigateToScoreBucketTable(
  navigate: NavigateFunction,
  slice: Pick<ScoreDistributionSlice, 'minScore' | 'maxScore' | 'isLastBucket'>,
  config: AnalyticsConfig,
) {
  navigateToFilteredTable(navigate, buildScoreBucketFilters(slice, config.filterScoreField));
}

export { navigateToScoreBucketTable as navigateToGdaScoreBucketTable };

export function navigateToSelectedEntityTable(
  navigate: NavigateFunction,
  view: string,
  selectedEntityId: string,
) {
  const filter = buildSelectedEntityFilter(view, selectedEntityId);
  if (!filter) return;
  navigateToFilteredTable(navigate, [filter]);
}

function buildSelectedEntityFilter(view: string, selectedEntityId: string): SummaryFilter | null {
  if (!selectedEntityId) return null;

  switch (view) {
    case 'DISEASES':
      return {
        name: 'diseaseUMLSCUI',
        operator: 'equals',
        type: 'string',
        value: selectedEntityId,
        isEvidence: false,
      };
    case 'GENES':
      return {
        name: 'geneNcbiID',
        operator: 'equals',
        type: 'string',
        value: selectedEntityId,
        isEvidence: false,
      };
    case 'VARIANTS':
      return {
        name: 'variantStrID',
        operator: 'startsWith',
        type: 'string',
        value: selectedEntityId,
        isEvidence: false,
      };
    default:
      return null;
  }
}
